<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BCard,
  BCardText,
  BCardBody,
  BCardTitle,
  BCardSubTitle,
  BCardFooter,
  BCardHeader,
  BCol,
  BRow,
} from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { watch, ref, computed } from '@vue/composition-api';
import store from '@/store';
import moment from 'moment';

import FormInput from '@/views/apps/fbr-invoice/components/form/FormInput.vue';
import FormTextArea from '@/views/apps/fbr-invoice/components/form/FormTextArea.vue';
import CustomerDropdownField from '@/views/apps/fbr-invoice/components/form/CustomerDropdownField.vue';
import SalesTypeDropdownField from '@/views/apps/fbr-invoice/components/form/SalesTypeDropdownField.vue';
import InvoiceFormDetails from './InvoiceFormDetails.vue';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import { api } from '@/utils/api';

export default {
  name: 'InvoiceForm',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BCard,
    BCardBody,
    BCardTitle,
    BCardSubTitle,
    BCardText,
    BCardFooter,
    BCardHeader,
    BCol,
    BRow,
    ToastificationContent,

    // Form Validation
    ValidationObserver,
    ValidationProvider,

    // Custom Components
    FormInput,
    FormTextArea,
    CustomerDropdownField,
    SalesTypeDropdownField,
    InvoiceFormDetails,
  },
  model: {
    prop: 'formData',
    event: 'update:form-data',
  },
  props: {
    formData: {
      type: Object | null,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const businessID = computed(() => store.state.user.businessID);

    /////////////////////////
    // Hooks
    /////////////////////////
    const toast = useToast();
    const userID = computed(() => store.state.user.userID || store.state.user.id);

    /////////////////////////
    // Reactive References
    /////////////////////////
    const selectedCustomer = ref(null);
    const invoiceFormRef = ref(null);
    const invoiceFormData = ref(
      props.formData
        ? props.formData
        : {
            entity: {
              id: 0,
              businessID: businessID,
              customerID: 0,
              customerCode: '',
              docNo: '',
              tranDate: moment().format('YYYY-MM-DD'),
              salesTypeID: 11,
              salesTaxInvoiceNo: '',
              sroScheduleNo: '',
              remarks: '',
              fbrInvoiceNo: '',
              grossAmount: 0,
              totalSalesTax: 0,
              notifiedValue: 0,
              totalFurtherTax: 0,
              totalAdvanceTax: 0,
              totalDiscount: 0,
              netAmount: 0,
              totalTax: 0,
              valWithoutTax: 0,
              valIncludingTax: 0,
            },
            details: [],
          }
    );

    /////////////////////////
    // Computed Properties
    /////////////////////////
    const furtherTaxPercent = computed(() => {
      console.log(
        '=== COMPUTED furtherTaxPercent',
        JSON.stringify(selectedCustomer.value, null, 2)
      );
      return selectedCustomer.value?.metaFurtherTax || 0;
    });

    const totalGrossAmount = computed(() => {
      return invoiceFormData.value.details
        .reduce((total, detail) => {
          return total + (detail.grossAmount || 0);
        }, 0)
        .toLocaleString('en-US', {
          currency: 'PKR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    });

    const totalDiscountAmount = computed(() => {
      return invoiceFormData.value.details
        .reduce((total, detail) => {
          return total + (detail.discountAmount || 0);
        }, 0)
        .toLocaleString('en-US', {
          currency: 'PKR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    });

    const totalTaxAmount = computed(() => {
      return invoiceFormData.value.details
        .reduce((total, detail) => {
          return total + (detail.totalTax || 0);
        }, 0)
        .toLocaleString('en-US', {
          currency: 'PKR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    });
    
    const totalNotifiedAmount = computed(() => {
      return invoiceFormData.value.details
        .reduce((total, detail) => {
          return total + (detail.notifiedValue || 0);
        }, 0)
        .toLocaleString('en-US', {
          currency: 'PKR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    });

    const totalValWithoutTax = computed(() => {
      const gross = parseFloat(totalGrossAmount.value.replace(/,/g, ''));
      const discount = parseFloat(totalDiscountAmount.value.replace(/,/g, ''));
      return (gross - discount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    });

    const totalNetAmount = computed(() => {
      return invoiceFormData.value.details
        .reduce((total, detail) => {
          return total + (detail.valWithoutTax || 0) + (detail.totalTax || 0) + (detail.valIncludingTax || 0);
        }, 0)
        .toLocaleString('en-US', {
          currency: 'PKR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    });

    /////////////////////////
    // Watchers
    /////////////////////////
    watch(
      [invoiceFormData.value.details],
      (newVal) => {
        const calculatedTotals = calculateTotals(newVal);

        if (
          invoiceFormData.value.entity.grossAmount !== calculatedTotals.grossAmount ||
          invoiceFormData.value.entity.notifiedValue !== calculatedTotals.notifiedValue ||
          invoiceFormData.value.entity.totalDiscount !== calculatedTotals.totalDiscount ||
          invoiceFormData.value.entity.valWithoutTax !== calculatedTotals.valWithoutTax ||
          invoiceFormData.value.entity.totalTax !== calculatedTotals.totalTax ||
          invoiceFormData.value.entity.totalSalesTax !== calculatedTotals.totalSalesTax ||
          invoiceFormData.value.entity.totalFurtherTax !== calculatedTotals.totalFurtherTax ||
          invoiceFormData.value.entity.totalAdvanceTax !== calculatedTotals.totalAdvanceTax ||
          invoiceFormData.value.entity.valIncludingTax !== calculatedTotals.valIncludingTax ||
          invoiceFormData.value.entity.netAmount !== calculatedTotals.netAmount
        ) {
          invoiceFormData.value.entity.grossAmount = calculatedTotals.grossAmount;
          invoiceFormData.value.entity.notifiedValue = calculatedTotals.notifiedValue;
          invoiceFormData.value.entity.totalDiscount = calculatedTotals.totalDiscount;
          invoiceFormData.value.entity.valWithoutTax = calculatedTotals.valWithoutTax;
          invoiceFormData.value.entity.totalTax = calculatedTotals.totalTax;
          invoiceFormData.value.entity.totalSalesTax = calculatedTotals.totalSalesTax;
          invoiceFormData.value.entity.totalFurtherTax = calculatedTotals.totalFurtherTax;
          invoiceFormData.value.entity.totalAdvanceTax = calculatedTotals.totalAdvanceTax;
          invoiceFormData.value.entity.valIncludingTax = calculatedTotals.valIncludingTax;
          invoiceFormData.value.entity.netAmount = calculatedTotals.netAmount;
        }
      },
      { deep: true }
    );

    watch(
      invoiceFormData,
      (newVal) => {
        // console.log('=== WATCH formData from InvoiceForm', JSON.stringify(newVal, null, 2));
        emit('update:form-data', newVal);
      },
      { deep: true }
    );

    watch(
      furtherTaxPercent,
      (newVal) => {
        console.log('=== WATCH furtherTaxPercent', newVal);
      },
      { deep: true }
    );

    /////////////////////////
    // Methods
    /////////////////////////
    const detailsAreValid = () => {
      // Validate Length
      if (invoiceFormData.value.details.length === 0) {
        throw new Error('Please add at least one item');
      }

      // Validate Unique Items
      // const itemIDs = invoiceFormData.value.details.map((item) => item.itemID);
      // const uniqueItemIDs = [...new Set(itemIDs)];
      // if (itemIDs.length !== uniqueItemIDs.length) {
      //   throw new Error('Items must be unique - duplicate items found');
      // }

      // Validate Each Item
      for (const item of invoiceFormData.value.details) {
        if (item.itemID === 0) {
          throw new Error('Item is required');
        }

        if (item.qty === 0) {
          throw new Error('Quantity should be greater than 0');
        }

        if (item.rate === 0) {
          throw new Error('Unit Price is required');
        }

        // validate numeric fields
        const numericFields = {
          qty: 'Quantity',
          rate: 'Rate',
          discountPercent: 'Discount Percent',
          discountAmount: 'Discount Amount',
          netAmount: 'Net Amount',
          salesTaxPercent: 'Sales Tax Percent',
          salesTaxAmount: 'Sales Tax Amount',
          notifiedValue: 'Fixed/ Notified',
          furtherTaxPercent: 'Further Tax Percent',
          furtherTaxAmount: 'Further Tax Amount',
          addSalesTaxPercent: 'Add Sales Tax Percent',
          addSalesTaxAmount: 'Add Sales Tax Amount',
          valWithoutTax: 'Value Without Tax',
          totalTax: 'Total Tax',
          tax236GValue: '236G/H %',
          valIncludingTax: '236G/H Amount',
        };

        for (const [key, value] of Object.entries(numericFields)) {
          if (isNaN(item[key])) {
            throw new Error(`${value} should be a number`);
          }

          if (item[key] < 0) {
            if (item[key] < 0) {
              throw new Error(`${value} cannot be less than 0`);
            }
          }
        }

        if (item.salesTaxPercent < 18 && item.sroScheduleNo.trim() === '') {
          throw new Error('SRO Schedule Number is required');
        }
        if (item.salesTaxPercent < 18 && item.sroItemSerialNo.trim() === '') {
          throw new Error('SRO Item Serial Number is required');
        }
      }

      return true;
    };

    const onSubmit = async () => {
      console.log('Emitting:', JSON.stringify(invoiceFormData.value, null, 2));

      try {
        if (detailsAreValid()) {
          console.log('=== Invoice form will emit');
          emit('submit');
        }
      } catch (error) {
        console.error('Error in validateDetails', error.message);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    };

    const calculateTotals = (newVal = null) => {
      const details = newVal || invoiceFormData.value.details || [];

      return details.reduce(
        (totals, detail) => {
          totals.grossAmount += detail.grossAmount || 0;
          totals.notifiedValue += detail.notifiedValue || 0;
          totals.totalDiscount += detail.discountAmount || 0;
          totals.valWithoutTax += (detail.grossAmount || 0) - (detail.discountAmount || 0);
          totals.totalTax += detail.totalTax || 0;
          totals.totalSalesTax += detail.salesTaxAmount || 0;
          totals.totalFurtherTax += detail.furtherTaxAmount || 0;
          totals.totalAdvanceTax += detail.valIncludingTax || 0;
          totals.valIncludingTax += detail.valIncludingTax || 0;
          totals.netAmount += (detail.valWithoutTax || 0) + (detail.totalTax || 0) + (detail.valIncludingTax || 0);
          return totals;
        },
        {
          grossAmount: 0,
          notifiedValue: 0,
          totalDiscount: 0,
          valWithoutTax: 0,
          totalTax: 0,
          totalSalesTax: 0,
          totalFurtherTax: 0,
          totalAdvanceTax: 0,
          valIncludingTax: 0,
          netAmount: 0,
        }
      );
    };

    const onClickSubmit = () => {
      invoiceFormRef.value.dispatchEvent(new Event('submit', { cancelable: true }));
    };

    const onClickCancel = () => {
      if (
        confirm('Are you sure you want to cancel?', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
      ) {
        emit('cancel');
      }
    };

    const onSelectedCustomer = (customer) => {
      selectedCustomer.value = customer
        ? {
            ...customer,
            metaFurtherTax: customer.metaFurtherTax || 0,
          }
        : null;
    };

    const isUpdatingCnic = ref(false);

    const updateCustomerCnic = async () => {
      if (!selectedCustomer.value || selectedCustomer.value.category !== 'unregitered') return;
      if (!selectedCustomer.value.id) return;

      isUpdatingCnic.value = true;
      try {
        const payload = {
          entity: {
            ...selectedCustomer.value,
          },
        };

        const response = await api.post(
          `/CustomersFBR/Save/${businessID.value}?userID=${userID.value}`,
          payload
        );

        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Customer CNIC updated automatically',
            variant: 'success',
            icon: 'CheckCircleIcon',
          },
        });
      } catch (error) {
        console.error('Error updating CNIC:', error);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to auto-update customer CNIC',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        isUpdatingCnic.value = false;
      }
    };

    watch(
      selectedCustomer,
      (newVal) => {
        console.log('=== WATCH selectedCustomer', newVal);
      },
      { deep: true }
    );

    return {
      invoiceFormRef,
      invoiceFormData,
      selectedCustomer,
      isUpdatingCnic,
      updateCustomerCnic,
      furtherTaxPercent,
      totalGrossAmount,
      totalDiscountAmount,
      totalValWithoutTax,
      totalTaxAmount,
      totalNetAmount,
      totalNotifiedAmount,

      onSelectedCustomer,
      onClickCancel,
      onSubmit,
      calculateTotals,
      onClickSubmit,
    };
  },
};
</script>

<template>
  <div>
    <div class="resource-header border-bottom d-flex justify-content-end align-items-start mb-1">
      <div class="actions-container d-flex justify-content-between">
        <b-button
          type="button"
          variant="outline-default"
          class="mr-1"
          :disabled="loading"
          @click="onClickCancel"
        >
          Cancel
        </b-button>
        <b-button type="submit" variant="primary" :disabled="loading" @click="onClickSubmit">
          Submit
        </b-button>
      </div>
    </div>
    <validation-observer>
      <b-form
        slot-scope="{ validate }"
        @submit.prevent="validate().then(onSubmit)"
        ref="invoiceFormRef"
      >
        <b-card no-body>
          <b-card-header>
            <b-card-title>Customer Details</b-card-title>
          </b-card-header>

          <b-card-body>
            <b-row>
              <!-- Customer -->
              <b-col md="6">
                <customer-dropdown-field
                  v-model="invoiceFormData.entity.customerID"
                  :disabled="loading"
                  label="Customer"
                  name="Customer"
                  rules="required"
                  placeholder="Search and select customer..."
                  :show-add-customer="true"
                  @selected="onSelectedCustomer"
                />
              </b-col>

              <!-- Doc No -->
              <b-col md="6">
                <b-form-group label="Doc No">
                  <b-form-input
                    placeholder="System Generated"
                    v-model="invoiceFormData.entity.docNo"
                    readonly
                  >
                  </b-form-input>
                </b-form-group>
              </b-col>

              <!-- Transaction Date -->
              <b-col md="6">
                <form-input
                  v-model="invoiceFormData.entity.tranDate"
                  :disabled="loading"
                  label="Transaction Date"
                  name="Transaction Date"
                  rules="required"
                  type="date"
                />
              </b-col>

              <!-- CNIC Number (Only for Unregistered) -->
              <b-col md="6" v-if="selectedCustomer && selectedCustomer.category === 'unregitered'">
                <b-form-group>
                  <template #label>
                    CNIC Number
                    <b-spinner v-if="isUpdatingCnic" small class="ml-1" variant="primary" />
                  </template>
                  <b-form-input
                    v-model="selectedCustomer.cnicNumber"
                    disabled
                    placeholder="CNIC Number"
                  />
                </b-form-group>
              </b-col>

              <!-- Sales Type -->
              <b-col md="6">
                <sales-type-dropdown-field
                  v-model="invoiceFormData.entity.salesTypeID"
                  :disabled="loading"
                  label="Sales Type"
                  name="Sales Type"
                  rules="required"
                />
              </b-col>

              <!-- Sales Tax Invoice No -->
              <b-col md="6">
                <form-input
                  v-model="invoiceFormData.entity.salesTaxInvoiceNo"
                  :disabled="loading"
                  label="Sales Tax Invoice No"
                  name="Sales Tax Invoice No"
                  rules=""
                />
              </b-col>

              <!-- SRO Schedule No -->
              <!-- <b-col md="6">
                <form-input
                  v-model="invoiceFormData.entity.sroScheduleNo"
                  :disabled="loading"
                  label="SRO Schedule No"
                  name="SRO Schedule No"
                  rules=""
                />
              </b-col> -->

              <!-- Remarks -->
              <b-col md="12">
                <form-text-area
                  v-model="invoiceFormData.entity.remarks"
                  :disabled="loading"
                  label="Remarks"
                  name="Remarks"
                  placeholder="Enter any additional remarks..."
                  rules=""
                />
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <invoice-form-details
          v-model="invoiceFormData.details"
          :loading="loading"
          :further-tax-percent="furtherTaxPercent"
        />

        <!-- Summary Section -->
        <b-card no-body>
          <b-card-header>
            <b-card-title>Summary</b-card-title>
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="2">
                <div class="summary-item">
                  <label>Gross Amount:</label>
                  <span class="amount">{{
                    totalGrossAmount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}</span>
                </div>
              </b-col>
              <b-col md="2">
                <div class="summary-item">
                  <label>Total Discount:</label>
                  <span class="amount">{{ totalDiscountAmount }}</span>
                </div>
              </b-col>
              <b-col md="2">
                <div class="summary-item">
                  <label>Value:</label>
                  <span class="amount">{{ totalValWithoutTax }}</span>
                </div>
              </b-col>
              <b-col md="2">
                <div class="summary-item">
                  <label>Fixed/Notified:</label>
                  <span class="amount">{{ totalNotifiedAmount }}</span>
                </div>
              </b-col>
              <b-col md="2">
                <div class="summary-item">
                  <label>Total Tax:</label>
                  <span class="amount">{{ totalTaxAmount }}</span>
                </div>
              </b-col>
              <b-col md="2">
                <div class="summary-item">
                  <label>Net Amount:</label>
                  <span class="amount font-weight-bold">{{ totalNetAmount }}</span>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-form>
    </validation-observer>
  </div>
</template>

<style lang="scss" scoped>
.resource-header {
  position: sticky;
  left: 0;
  top: 60px;
  padding: 1.3rem 0 1.3rem 0;
  background-color: var(--light);
  z-index: 999;
}

@media (max-width: 576px) {
  .resource-header {
    flex-direction: column-reverse !important;
    padding: 1.3rem 0 0 0;

    & > div:not(:first-child) {
      width: 100%;
      margin-bottom: 1rem;
    }
  }
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;

  label {
    font-weight: 500;
    color: #6c757d;
    margin: 0;
  }

  .amount {
    font-size: 1.1rem;
    font-weight: 600;
    color: #495057;

    &.font-weight-bold {
      font-weight: 700;
      color: #28a745;
    }
  }
}

.b-card {
  margin-bottom: 1rem;
}
</style>
